import React, { useState } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemText, Paper, Divider, Button, Container, Grid, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SEO from '../../components/common/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../../components/common/StructuredData';
import AEOFAQ from '../../components/common/AEOFAQ';

const articles = [
  {
    id: 'nexus-thresholds',
    title: 'What is Economic Nexus? Why It Matters for Your Business',
    snippet: 'Learn how economic nexus laws impact your sales tax obligations in different states. Complete guide to understanding economic nexus thresholds and compliance requirements.',
    content: (
      <>
        <Typography variant="h4" gutterBottom component="h1">What is Economic Nexus? Why It Matters for Your Business</Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
          Economic nexus is a sales tax concept that determines when your business must collect and remit sales tax in a state, based on your sales volume or transaction count—even if you have no physical presence there. With the rise of e-commerce, most states have adopted economic nexus laws. If you exceed a state's threshold (e.g., $100,000 in sales or 200 transactions), you're required to register, collect, and remit sales tax in that state.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2" sx={{ mt: 4 }}>Understanding Economic Nexus</Typography>
        <Typography variant="body1" paragraph>
          Economic nexus, also known as "click-through nexus" or "cookie nexus," was established by the U.S. Supreme Court in the 2018 South Dakota v. Wayfair decision. This ruling allowed states to require out-of-state sellers to collect sales tax even without physical presence in the state, as long as they meet certain economic thresholds.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">Current Economic Nexus Thresholds by State</Typography>
        <Typography variant="body1" paragraph>
          Each state sets its own economic nexus thresholds. Here are the current thresholds for major states:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph><strong>California:</strong> $500,000 in sales or 200+ transactions</Typography>
          <Typography component="li" paragraph><strong>Texas:</strong> $500,000 in sales</Typography>
          <Typography component="li" paragraph><strong>Florida:</strong> $100,000 in sales or 200+ transactions</Typography>
          <Typography component="li" paragraph><strong>New York:</strong> $500,000 in sales or 100+ transactions</Typography>
          <Typography component="li" paragraph><strong>Illinois:</strong> $100,000 in sales or 200+ transactions</Typography>
        </Box>

        <Typography variant="h3" gutterBottom component="h2">Why Economic Nexus Matters</Typography>
        <Typography variant="body1" paragraph>
          Failing to establish nexus when required can result in:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph>Significant back taxes and penalties</Typography>
          <Typography component="li" paragraph>Use tax assessments on customers</Typography>
          <Typography component="li" paragraph>Legal and compliance risks</Typography>
          <Typography component="li" paragraph>Damage to business reputation</Typography>
        </Box>

        <Typography variant="h3" gutterBottom component="h2">How GoTruTax Helps</Typography>
        <Typography variant="body1" paragraph>
          Our AI-powered platform automatically monitors your sales activity across all states and alerts you when you approach or exceed economic nexus thresholds. We help you:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph>Track sales by state in real-time</Typography>
          <Typography component="li" paragraph>Receive automated nexus alerts</Typography>
          <Typography component="li" paragraph>Handle state registrations automatically</Typography>
          <Typography component="li" paragraph>Maintain compliance across all jurisdictions</Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mt: 3 }}>
          <strong>Actionable Tip:</strong> Use our app to track your sales by state and get notified when you approach a nexus threshold! Start monitoring your economic nexus obligations today with our free tier.
        </Typography>
      </>
    ),
    keywords: ['economic nexus', 'sales tax nexus', 'nexus thresholds', 'multi-state tax', 'Wayfair decision', 'sales tax compliance'],
    readingTime: '5 min read',
    publishDate: '2024-01-15',
    author: 'GoTruTax Tax Experts'
  },
  {
    id: 'food-taxability',
    title: 'Is Food Taxable? Surprising State-by-State Differences',
    snippet: 'Food taxability varies widely across states. Some tax groceries, others don\'t, and some tax only certain foods. Complete guide to food taxation rules.',
    content: (
      <>
        <Typography variant="h4" gutterBottom component="h1">Is Food Taxable? Surprising State-by-State Differences</Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
          Food taxability is one of the most complex areas of sales tax compliance. While most people assume groceries are always tax-free, the reality is much more nuanced. States have different rules for groceries, prepared food, candy, and even specific food items. Understanding these differences is crucial for accurate tax calculations and compliance.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">Food Taxation Categories</Typography>
        <Typography variant="body1" paragraph>
          Food items generally fall into these categories:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph><strong>Grocery Food:</strong> Basic food items like milk, bread, meat, and produce</Typography>
          <Typography component="li" paragraph><strong>Prepared Food:</strong> Restaurant meals, hot food, catering</Typography>
          <Typography component="li" paragraph><strong>Candy and Snacks:</strong> Chocolate, gum, chips, soda</Typography>
          <Typography component="li" paragraph><strong>Dietary Supplements:</strong> Vitamins, protein powders</Typography>
          <Typography component="li" paragraph><strong>Alcoholic Beverages:</strong> Beer, wine, liquor (often taxed separately)</Typography>
        </Box>

        <Typography variant="h3" gutterBottom component="h2">State-by-State Food Tax Rules</Typography>

        <Typography variant="h4" gutterBottom component="h3">States That Don't Tax Groceries</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph><strong>California:</strong> No tax on groceries, but candy and snacks are taxable</Typography>
          <Typography component="li" paragraph><strong>New York:</strong> Groceries exempt, but prepared food taxed at 8.875%</Typography>
          <Typography component="li" paragraph><strong>Texas:</strong> No state tax on food, but local taxes may apply</Typography>
          <Typography component="li" paragraph><strong>Florida:</strong> Groceries tax-free, but some counties add local tax</Typography>
        </Box>

        <Typography variant="h4" gutterBottom component="h3">States That Tax Some Food Items</Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph><strong>Illinois:</strong> 1% tax on candy, 6.25% on soft drinks</Typography>
          <Typography component="li" paragraph><strong>Iowa:</strong> Candy taxable at 6%, groceries exempt</Typography>
          <Typography component="li" paragraph><strong>Tennessee:</strong> Food tax-free, but candy and snacks taxed</Typography>
        </Box>

        <Typography variant="h3" gutterBottom component="h2">Special Cases and Exceptions</Typography>
        <Typography variant="body1" paragraph>
          Some states have unique rules:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph><strong>Georgia:</strong> Taxable groceries can be refunded with receipts</Typography>
          <Typography component="li" paragraph><strong>Louisiana:</strong> Different rates for different parishes</Typography>
          <Typography component="li" paragraph><strong>Minnesota:</strong> Food tax-free, but some local taxes apply</Typography>
        </Box>

        <Typography variant="h3" gutterBottom component="h2">How GoTruTax Handles Food Taxation</Typography>
        <Typography variant="body1" paragraph>
          Our AI-powered platform automatically determines food taxability based on:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph>Product category classification</Typography>
          <Typography component="li" paragraph>State-specific tax rules</Typography>
          <Typography component="li" paragraph>Local tax jurisdictions</Typography>
          <Typography component="li" paragraph>Real-time rate updates</Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mt: 3 }}>
          <strong>Actionable Tip:</strong> Our app's product taxability checker helps you instantly see how your products are taxed in every state. Never guess about food taxation again!
        </Typography>
      </>
    ),
    keywords: ['food taxability', 'grocery tax', 'food sales tax', 'candy tax', 'prepared food tax', 'state tax differences'],
    readingTime: '7 min read',
    publishDate: '2024-01-20',
    author: 'GoTruTax Compliance Team'
  },
  {
    id: 'filing-mistakes',
    title: 'Top 5 Sales Tax Filing Mistakes (and How to Avoid Them)',
    snippet: 'Avoid common sales tax filing pitfalls including missing deadlines, wrong rates, and compliance errors. Complete guide to error-free tax filing.',
    content: (
      <>
        <Typography variant="h4" gutterBottom component="h1">Top 5 Sales Tax Filing Mistakes (and How to Avoid Them)</Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
          Sales tax compliance is complex, and even experienced businesses make mistakes. According to the National Conference of State Legislatures, businesses lose millions annually due to sales tax errors. Here are the most common filing mistakes and how to avoid them.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">1. Missing Filing Deadlines</Typography>
        <Typography variant="body1" paragraph>
          Each state has different filing frequencies and deadlines. Missing a deadline can result in late penalties, interest charges, and even business license suspension.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>How to Avoid:</strong> Set up automated reminders and use compliance software that tracks all your filing deadlines across states.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">2. Filing in the Wrong State</Typography>
        <Typography variant="body1" paragraph>
          Many businesses file in states where they have physical presence but forget about economic nexus obligations in other states.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>How to Avoid:</strong> Maintain a comprehensive nexus tracking system and regularly audit your state obligations.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">3. Using Outdated Tax Rates</Typography>
        <Typography variant="body1" paragraph>
          Tax rates change frequently due to legislative updates, local elections, and special assessments. Using old rates can lead to under or over-collection.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>How to Avoid:</strong> Use automated rate update systems that pull real-time data from official state sources.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">4. Not Tracking Exemption Certificates</Typography>
        <Typography variant="body1" paragraph>
          Businesses often forget to track and validate exemption certificates, leading to incorrect tax collection or collection when exemptions should apply.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>How to Avoid:</strong> Implement digital certificate management systems with validation and expiration tracking.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">5. Ignoring Local (City/County) Taxes</Typography>
        <Typography variant="body1" paragraph>
          Many states have local tax jurisdictions with different rates. Failing to account for these can result in compliance gaps.
        </Typography>
        <Typography variant="body1" paragraph>
          <strong>How to Avoid:</strong> Use comprehensive tax calculation software that includes all local tax jurisdictions.
        </Typography>

        <Typography variant="h3" gutterBottom component="h2">The Cost of Sales Tax Mistakes</Typography>
        <Typography variant="body1" paragraph>
          According to a 2023 study by the Tax Foundation:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph>Average penalty for late filing: $500-$5,000 per state</Typography>
          <Typography component="li" paragraph>Under-collection penalties: 5-25% of unpaid tax</Typography>
          <Typography component="li" paragraph>Audit interest rates: 0.5-2% per month</Typography>
          <Typography component="li" paragraph>Business license suspension risk</Typography>
        </Box>

        <Typography variant="h3" gutterBottom component="h2">How GoTruTax Prevents These Mistakes</Typography>
        <Typography variant="body1" paragraph>
          Our AI-powered platform eliminates common filing errors through:
        </Typography>
        <Box component="ul" sx={{ pl: 3 }}>
          <Typography component="li" paragraph>Automated deadline tracking and reminders</Typography>
          <Typography component="li" paragraph>Real-time nexus monitoring</Typography>
          <Typography component="li" paragraph>Automatic rate updates from official sources</Typography>
          <Typography component="li" paragraph>Digital exemption certificate management</Typography>
          <Typography component="li" paragraph>Comprehensive local tax rate inclusion</Typography>
        </Box>

        <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mt: 3 }}>
          <strong>Actionable Tip:</strong> Our app automates reminders, keeps rates up to date, and helps you file accurately—saving you time and money. Start your error-free tax compliance journey today!
        </Typography>
      </>
    ),
    keywords: ['sales tax mistakes', 'tax filing errors', 'compliance mistakes', 'tax penalties', 'filing deadlines', 'tax rates'],
    readingTime: '6 min read',
    publishDate: '2024-01-25',
    author: 'GoTruTax Compliance Team'
  }
];

export default function BlogsKnowledge() {
  const [selectedId, setSelectedId] = useState(articles[0].id);
  const selectedArticle = articles.find(a => a.id === selectedId);
  const featuredArticle = articles[0];

  // Breadcrumb navigation
  const breadcrumbs = [
    { name: 'Home', url: 'https://gotrutax.com' },
    { name: 'Resources', url: 'https://gotrutax.com/resources/blogs-knowledge' },
    { name: selectedArticle?.title || 'Article', url: `https://gotrutax.com/resources/blogs-knowledge#${selectedId}` }
  ];

  return (
    <>
      {/* SEO Optimization for Knowledge Base */}
      <SEO
        title="Sales Tax Knowledge Base - Complete Guide to Tax Compliance | GoTruTax"
        description="Comprehensive sales tax knowledge base with guides on economic nexus, food taxability, filing mistakes, and multi-state compliance. Expert insights for business tax compliance."
        keywords={[
          'sales tax knowledge base',
          'tax compliance guide',
          'economic nexus guide',
          'food taxability rules',
          'sales tax filing tips',
          'multi-state tax compliance',
          'business tax education',
          'sales tax best practices'
        ]}
        type="article"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          'headline': 'Sales Tax Knowledge Base - Complete Guide to Tax Compliance',
          'description': 'Comprehensive guides on sales tax compliance, economic nexus, food taxability, and multi-state tax requirements',
          'url': 'https://gotrutax.com/resources/blogs-knowledge',
          'datePublished': '2024-01-15',
          'dateModified': '2025-01-22',
          'author': {
            '@type': 'Organization',
            'name': 'GoTruTax',
            'url': 'https://gotrutax.com'
          },
          'publisher': {
            '@type': 'Organization',
            'name': 'GoTruTax',
            'logo': {
              '@type': 'ImageObject',
              'url': 'https://gotrutax.com/logo.svg'
            }
          },
          'mainEntityOfPage': {
            '@type': 'WebPage',
            '@id': 'https://gotrutax.com/resources/blogs-knowledge'
          },
          'keywords': 'sales tax, tax compliance, economic nexus, food taxability, multi-state tax'
        }}
      />

      {/* Article Schema for Selected Article */}
      {selectedArticle && (
        <ArticleSchema
          headline={selectedArticle.title}
          description={selectedArticle.snippet}
          image="https://gotrutax.com/hero-image.png"
          datePublished={selectedArticle.publishDate}
          dateModified="2025-01-22"
          author={{
            name: selectedArticle.author,
            url: 'https://gotrutax.com'
          }}
          publisher={{
            name: 'GoTruTax',
            logo: 'https://gotrutax.com/logo.svg'
          }}
          mainEntityOfPage={`https://gotrutax.com/resources/blogs-knowledge#${selectedId}`}
          keywords={selectedArticle.keywords}
        />
      )}

      {/* Breadcrumb Schema */}
      <BreadcrumbSchema items={breadcrumbs} />

      <Box sx={{ bgcolor: 'background.default' }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            py: { xs: 6, md: 8 },
            background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.25)}, ${alpha(theme.palette.secondary.light, 0.25)})`,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: theme => `radial-gradient(circle at 15% 20%, ${alpha(theme.palette.primary.main, 0.18)}, transparent 55%),
                radial-gradient(circle at 85% 25%, ${alpha(theme.palette.secondary.main, 0.18)}, transparent 50%)`,
              opacity: 0.9
            }
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h1" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, mb: 2 }}>
                  Sales Tax Knowledge Base
                </Typography>
                <Typography variant="h2" sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, fontWeight: 400, color: 'text.secondary', mb: 3 }}>
                  Practical guides, checklists, and expert insights to help you stay ahead of multi-state tax compliance.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${articles.length} expert guides`} size="small" color="primary" variant="outlined" />
                  <Chip label="Updated for 2025 rules" size="small" variant="outlined" />
                  <Chip label="Actionable tips and examples" size="small" variant="outlined" />
                </Box>
              </Grid>
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: theme => `1px solid ${theme.palette.divider}`,
                    boxShadow: theme => `0 16px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                  }}
                >
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                    Featured Guide
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 1, mb: 1 }}>
                    {featuredArticle.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {featuredArticle.snippet}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip label={featuredArticle.readingTime} size="small" variant="outlined" />
                    <Chip label={featuredArticle.author} size="small" variant="outlined" />
                  </Box>
                  <Button variant="contained" color="primary" component="a" href="#article-list">
                    Explore articles
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={4} lg={3}>
              <Paper
                elevation={0}
                id="article-list"
                sx={{
                  position: { md: 'sticky' },
                  top: { md: 96 },
                  p: 3,
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  boxShadow: theme => `0 12px 28px ${alpha(theme.palette.common.black, 0.06)}`
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
                  Tax Compliance Guides
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Curated playbooks and explainers built for operators and finance teams.
                </Typography>
                <List>
                  {articles.map(article => (
                    <ListItem key={article.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        selected={selectedId === article.id}
                        onClick={() => setSelectedId(article.id)}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: selectedId === article.id ? 'primary.main' : 'divider',
                          alignItems: 'flex-start',
                          bgcolor: theme => (selectedId === article.id ? alpha(theme.palette.primary.main, 0.08) : 'transparent'),
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.1)
                          },
                          '&.Mui-selected': {
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                            '&:hover': { bgcolor: theme => alpha(theme.palette.primary.main, 0.16) }
                          }
                        }}
                      >
                        <ListItemText
                          primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: selectedId === article.id ? 700 : 600 }}>
                              {article.title}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {article.snippet}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip label={article.readingTime} size="small" variant="outlined" />
                                <Chip label={article.author} size="small" variant="outlined" />
                              </Box>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8} lg={9}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 3,
                  bgcolor: 'background.paper',
                  border: theme => `1px solid ${theme.palette.divider}`,
                  boxShadow: theme => `0 16px 36px ${alpha(theme.palette.common.black, 0.08)}`
                }}
              >
                {selectedArticle && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="overline" color="primary" sx={{ fontWeight: 700 }}>
                        {selectedArticle.readingTime} | By {selectedArticle.author} | Published {selectedArticle.publishDate}
                      </Typography>
                      <Divider sx={{ mt: 2 }} />
                    </Box>

                    {selectedArticle.content}

                    <Divider sx={{ my: 4 }} />

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        Related Topics
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedArticle.keywords.map(keyword => (
                          <Chip key={keyword} label={keyword} variant="outlined" size="small" />
                        ))}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        textAlign: 'center',
                        mt: 4,
                        p: { xs: 3, md: 4 },
                        borderRadius: 3,
                        background: theme => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.secondary.main, 0.12)})`,
                        border: theme => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}
                    >
                      <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 700 }}>
                        Ready to simplify your sales tax compliance?
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        Join thousands of businesses using GoTruTax for automated tax compliance.
                      </Typography>
                      <Button variant="contained" color="primary" size="large" href="/register" sx={{ mr: 2 }}>
                        Start Free Trial
                      </Button>
                      <Button variant="outlined" color="primary" size="large" href="/contact">
                        Contact Sales
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: { xs: 6, md: 8 } }}>
            <AEOFAQ
              faqs={[
                {
                  question: "How often should I review my sales tax compliance?",
                  answer: "Review your sales tax compliance quarterly at minimum, but ideally monthly. States frequently change laws, and economic nexus thresholds can be met quickly with e-commerce growth.",
                  category: "Compliance"
                },
                {
                  question: "What are the consequences of sales tax non-compliance?",
                  answer: "Non-compliance can result in back taxes, penalties up to 25% of unpaid tax, interest charges, business license suspension, and potential criminal charges in severe cases.",
                  category: "Risk Management"
                },
                {
                  question: "How do I know if I have economic nexus in a state?",
                  answer: "You have economic nexus if you exceed the state's sales threshold (typically $100,000-$500,000) or transaction count (usually 200+). Use automated tracking tools to monitor this.",
                  category: "Nexus"
                }
              ]}
              title="Common Sales Tax Compliance Questions"
            />
          </Box>
        </Container>
      </Box>
    </>
  );
} 
